define(['lodash', 'coreUtils', 'gridComponent/core/enums'],
    function (_, coreUtils, enums) {
        'use strict';

        const wixLinkRenderer = coreUtils.linkRenderer;
        const wixLinkTypes = coreUtils.linkTypes;
        const dateTimeUtils = coreUtils.dateTimeUtils;
        const FieldType = enums.FieldType;
        const CellRenderers = {
            byFieldType: {}
        };

        const DEPRECATED_MEDIA_SRC_REGEX = /^image:\/\/([^\/]+)\/([^\/]+)\/(\d+)_(\d+)\/(.*)/;
        const DEPRECATED_MEDIA_SRC_TEST_REGEX = /^image:\/\//;
        const MEDIA_SRC_REGEX = /^wix:image:\/\/([^\/]+)\/([^\/]+)(\/([^\/]+))?\/#originWidth=(\d+)&originHeight=(\d+)(.*)/;
        const MEDIA_SRC_TEST_REGEX = /^wix:image:\/\//;

        function getStaticUrlFromMediaUrl(mediaToStaticUrlConverter, url, width, height) {
            return function (regex) {
                const match = url.match(regex);
                const version = match[1];
                const fileName = match[2];
                const staticUrl = `${mediaToStaticUrlConverter(fileName)}/${version}/fit/w_${width},h_${height}/${fileName}`;
                return staticUrl;
            };
        }

        function getStaticUrl(mediaToStaticUrlConverter, value, width, height) {
            if (_.isObject(value)) {
                if (coreUtils.urlUtils.isExternalUrl(value.uri)) {
                    return value.uri;
                }
                return `${mediaToStaticUrlConverter(value.uri)}/v1/fit/w_${width},h_${height}/${value.uri}`;
            } else if (_.isString(value)) {
                const convert = getStaticUrlFromMediaUrl(mediaToStaticUrlConverter, value, width, height);
                if (MEDIA_SRC_TEST_REGEX.test(value)) {
                    return convert(MEDIA_SRC_REGEX);
                } else if (DEPRECATED_MEDIA_SRC_TEST_REGEX.test(value)) {
                    return convert(DEPRECATED_MEDIA_SRC_REGEX);
                }
            }
            return value;
        }

        CellRenderers.byFieldType[FieldType.IMAGE] = function (props, params) {
            const mediaToStaticUrlConverter = props.getMediaFullStaticUrl;
            const width = Math.floor(params.column.actualWidth);
            const height = params.node.rowHeight;
            const url = getStaticUrl(mediaToStaticUrlConverter, params.value.data, width, height);
            const img = window.document.createElement('img');
            img.classList.add('ag-cell-image');
            if (!_.isNull(url)) {
                img.src = url;
            }
            return img;
        };

        CellRenderers.byFieldType[FieldType.DATE] = function (props, params) {
            const dateFormat = props.compProp.dateFormat;
            let dateString = '';
            if (!_.isNil(params.value.data)) {
                let date = new Date(params.value.data);
                if (_.isNaN(date.getTime())) {
                    date = new Date();
                }
                dateString = dateTimeUtils.dateFormat(date, dateFormat);
            }
            return window.document.createTextNode(dateString);
        };

        CellRenderers.byFieldType[FieldType.BOOLEAN] = function (props, params) {
            const value = params.value.data ? 'Yes' : 'No';
            return window.document.createTextNode(value);
        };

        CellRenderers.byFieldType[FieldType.RICH_TEXT] = function (props, params) {
            const div = window.document.createElement('div');
            div.classList.add('ag-cell-richtext');
            div.innerHTML = params.value.data;
            _.forEach(div.querySelectorAll('a'), el => updateLinkProps(el, {
                url: el.attributes.href.value,
                type: wixLinkTypes.EXTERNAL_LINK,
                target: props.compProp.linkTarget
            }, props.linkRenderInfo, props.rootNavigationInfo, true));

            return div;
        };

        CellRenderers.valueRenderer = function (props, params) {
            const value = _.get(params, 'value.data');
            return window.document.createTextNode(value);
        };

        CellRenderers.render = _.curry(function (props, column, params) {
            if (_.isUndefined(params.value.data)) {
                return '';
            }
            let contentParent;
            const cellDiv = contentParent = renderContentWrapper(props, params);
            if (hasLink(column, params)) {
                const link = renderLinkFromProps(params, props.linkRenderInfo, props.rootNavigationInfo);
                contentParent.appendChild(link);
                contentParent = link;
            }
            if (isTextField(column.type)) {
                const cellTextWrapperDiv = window.document.createElement('div');
                cellTextWrapperDiv.classList.add('cell-text-wrapper');
                const cellTextDiv = renderCellTextDiv();
                cellTextWrapperDiv.appendChild(cellTextDiv);
                contentParent.appendChild(cellTextWrapperDiv);
                contentParent = cellTextDiv;
            }
            const contentDiv = renderContent(column.type, props, params);
            contentParent.appendChild(contentDiv);
            return cellDiv;
        });

        function renderContentWrapper(props, params) {
            const columns = props.compProp.columns;
            const columnId = params.column.colId;
            const contentWrapper = window.document.createElement('div');
            contentWrapper.classList.add('cell-content-wrapper');
            if (isFirstColumn(columns, columnId)) {
                contentWrapper.classList.add('first-column');
            } else if (isLastColumn(columns, columnId)) {
                contentWrapper.classList.add('last-column');
            }
            return contentWrapper;
        }

        function renderCellTextDiv() {
            const cellTextDiv = window.document.createElement('div');
            cellTextDiv.classList.add('ag-cell-text');
            return cellTextDiv;
        }

        function renderLinkFromProps(params, linkRenderInfo, rootNavigationInfo) {
            const a = window.document.createElement('a');
            return updateLinkProps(a, params.value.link, linkRenderInfo, rootNavigationInfo, false);
        }

        function updateLinkProps(el, link, linkRenderInfo, rootNavigationInfo, isInline) {
            const wixLink = wixLinkRenderer.renderLink(link, linkRenderInfo, rootNavigationInfo);
            _.assign(el, wixLink);
            el.classList.add(`ag-cell-link${isInline ? '-inline' : ''}`);
            return el;
        }

        function renderContent(fieldType, props, params) {
            const contentRenderer = CellRenderers.byFieldType[fieldType] || CellRenderers.valueRenderer;
            return contentRenderer(props, params);
        }

        function isTextField(fieldType) {
            return fieldType !== FieldType.IMAGE;
        }

        function isFirstColumn(columnsArray, columnId) {
            return columnsArray[0].id === columnId;
        }

        function isLastColumn(columnsArray, columnId) {
            return columnsArray[columnsArray.length - 1].id === columnId;
        }

        function hasLink(column, params) {
            return !(_.isUndefined(column.linkPath) || _.isEmpty(params.value.link));
        }

        return CellRenderers;
    }
);
